import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Btn, Input, Field, Badge } from "@/components/ui-kit";
import { HardDrive, Cloud, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";

const EMPTY = { provider: "emergent", bucket: "", region: "", access_key_id: "", secret_access_key: "", endpoint_url: "", path_prefix: "" };

export default function StorageSettings() {
  const [f, setF] = useState(EMPTY);
  const [meta, setMeta] = useState({});
  const [busy, setBusy] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const load = () => api.get("/settings/storage").then((r) => {
    const { media_counts, has_secret, updated_at, updated_by, ...rest } = r.data;
    setF({ ...EMPTY, ...rest }); setMeta({ media_counts, has_secret, updated_at, updated_by });
  }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const test = async () => {
    setBusy("test");
    try { const { data } = await api.post("/settings/storage/test", f); toast.success(data.provider === "emergent" ? "Emergent storage reachable" : `S3 OK · bucket ${data.bucket} (${data.endpoint})`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try { await api.put("/settings/storage", f); toast.success(`Media storage set to ${f.provider === "s3" ? "S3" : "Emergent"}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const Opt = ({ v, icon: Icon, title, desc }) => (
    <button type="button" data-testid={`storage-provider-${v}`} onClick={() => setF({ ...f, provider: v })}
      className={`flex flex-1 items-start gap-3 rounded-md border p-3 text-left ${f.provider === v ? "border-primary bg-primary/10" : "border-border hover:bg-accent/50"}`}>
      <Icon size={20} className={f.provider === v ? "text-primary" : "text-muted-foreground"} />
      <div><div className="text-sm font-bold">{title}</div><div className="text-xs text-muted-foreground">{desc}</div></div>
      {f.provider === v && <CheckCircle2 size={16} className="ml-auto text-primary" />}
    </button>
  );

  const counts = meta.media_counts || {};
  return (
    <div className="space-y-4" data-testid="storage-settings-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold uppercase tracking-tight sm:text-3xl"><HardDrive size={24} /> Media Storage</h1>
        <p className="text-sm text-muted-foreground">Where inward / repair / outward photos & videos are stored. Manager & Supervisor only. Existing files keep working from the provider they were saved to.</p>
      </div>

      <Card className="space-y-4 p-4">
        <div className="flex flex-col gap-2 sm:flex-row">
          <Opt v="emergent" icon={Cloud} title="Emergent Object Storage" desc="Built-in, zero setup. Good default." />
          <Opt v="s3" icon={ShieldCheck} title="Amazon S3 / S3-compatible" desc="Your own bucket: AWS S3, MinIO, Wasabi, Backblaze B2, on-prem." />
        </div>

        {f.provider === "s3" && (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="s3-config">
            <Field label="Bucket" required><Input data-testid="s3-bucket" value={f.bucket} onChange={set("bucket")} placeholder="repairops-media" /></Field>
            <Field label="Region"><Input data-testid="s3-region" value={f.region} onChange={set("region")} placeholder="ap-south-1" /></Field>
            <Field label="Access Key ID" required><Input data-testid="s3-access-key" value={f.access_key_id} onChange={set("access_key_id")} autoComplete="off" /></Field>
            <Field label={`Secret Access Key${meta.has_secret ? " (saved · leave blank to keep)" : ""}`} required={!meta.has_secret}>
              <Input type="password" data-testid="s3-secret-key" value={f.secret_access_key} onChange={set("secret_access_key")} autoComplete="new-password" />
            </Field>
            <Field label="Endpoint URL (S3-compatible only)"><Input data-testid="s3-endpoint" value={f.endpoint_url} onChange={set("endpoint_url")} placeholder="https://minio.local:9000 — leave blank for AWS" /></Field>
            <Field label="Key Prefix (folder)"><Input data-testid="s3-prefix" value={f.path_prefix} onChange={set("path_prefix")} placeholder="workshop-a" /></Field>
            <p className="text-[11px] text-muted-foreground sm:col-span-2">Minimum IAM policy: <code>s3:PutObject</code>, <code>s3:GetObject</code> on <code>arn:aws:s3:::{f.bucket || "bucket"}/*</code>. Secrets are stored server-side and never returned to the browser.</p>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
          <div className="flex flex-wrap gap-2 font-mono text-[11px] text-muted-foreground">
            <Badge className="border-border">Emergent: {counts.emergent || 0} files</Badge>
            <Badge className="border-border">S3: {counts.s3 || 0} files</Badge>
            {meta.updated_at && <span>Updated by {meta.updated_by}</span>}
          </div>
          <div className="flex gap-2">
            <Btn variant="outline" onClick={test} disabled={!!busy} data-testid="storage-test-btn">{busy === "test" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Test Connection</Btn>
            <Btn onClick={save} disabled={!!busy} data-testid="storage-save-btn">{busy === "save" ? "Saving…" : "Save Settings"}</Btn>
          </div>
        </div>
      </Card>
    </div>
  );
}
